import { MapCoordinates } from '../data-access/map-config.tokens';

/** Whether `point` lies exactly on the segment `a`–`b`, ends included. */
function isOnSegment(
  point: MapCoordinates,
  a: MapCoordinates,
  b: MapCoordinates,
): boolean {
  const cross =
    (point.lng - a.lng) * (b.lat - a.lat) -
    (point.lat - a.lat) * (b.lng - a.lng);
  if (cross !== 0) {
    return false;
  }

  return (
    point.lat >= Math.min(a.lat, b.lat) &&
    point.lat <= Math.max(a.lat, b.lat) &&
    point.lng >= Math.min(a.lng, b.lng) &&
    point.lng <= Math.max(a.lng, b.lng)
  );
}

/**
 * Even-odd containment test for a `{ lat, lng }` ring, so drive-time-band membership
 * can be decided without a mapping library.
 *
 * Boundary convention: **the outline belongs to the ring.** A point lying exactly on an
 * edge or on a vertex returns `true`, tested before the crossing count. Left to the
 * crossing count alone such a point would resolve by which side of the ring the edge
 * happens to lie on — inside on a west edge, outside on an east one — so a station on a
 * band's outline would land in a band or not depending on where it touched it.
 *
 * The ring is treated as closed whether or not it repeats its first vertex; fewer than
 * three vertices encloses nothing. Coordinates are compared as planar degrees, which is
 * why a ring must be small enough that its curvature is below the resolution it is
 * delivered at, and why it must not span the ±180° meridian.
 */
export function isPointInRing(
  point: MapCoordinates,
  ring: readonly MapCoordinates[],
): boolean {
  if (ring.length < 3) {
    return false;
  }

  let inside = false;
  for (let i = 0, previous = ring.length - 1; i < ring.length; previous = i++) {
    const a = ring[previous];
    const b = ring[i];

    if (isOnSegment(point, a, b)) {
      return true;
    }

    // Half-open latitude span (a inclusive, b exclusive) so an edge pair meeting at a
    // vertex on the point's latitude contributes one crossing, not zero or two.
    const straddles = a.lat > point.lat !== b.lat > point.lat;
    if (!straddles) {
      continue;
    }

    const lngAtPointLat =
      a.lng + ((point.lat - a.lat) * (b.lng - a.lng)) / (b.lat - a.lat);
    if (point.lng < lngAtPointLat) {
      inside = !inside;
    }
  }

  return inside;
}
